import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { checkRequest } from '../../api/generic/checkRequest';
import { unAuthVisitUrl, visitUrl } from '../../api/generic/visitUrl';
import { View } from '../../common/constants/ViewEnum';
import { useAccountCreationContext } from '../../common/context/AccountCreationContext';
import { useAuthContext } from '../../common/context/AuthContext';
import { trackEvent } from '../../common/utils/analytics';
import { getGenderFromShoppingPreference, getShoppingPreferenceFromGender } from '../../common/utils/shoppingPreferenceLogic';
import ProgressBar from '../animations/ProgressBar';
import AnalysisView from '../views/account-creation/AnalysisView';
import LoginView from '../views/auth/LoginView';
import RegisterView from '../views/auth/RegisterView';
import ResetPasswordView from '../views/auth/ResetPasswordView';
import SizeRecommandationView from '../views/SizeRecommandationView';
import { WidgetModalProps } from './types';
import WidgetHeader from './WidgetHeader';

const WidgetModal: FC<WidgetModalProps> = (props) => {
	const { isAuthenticated, isOnboarded, token, logout } = useAuthContext();
	const { shoppingPreference, setShoppingPreference } = useAccountCreationContext();
	const { t } = useTranslation();

	// Debug logging for shopping preference changes
	React.useEffect(() => {
		console.debug('🔍 [WIDGET_MODAL] Shopping preference changed:', shoppingPreference);
	}, [shoppingPreference]);

	// In development mode for widget target, open by default
	const shouldOpenByDefault = process.env.NODE_ENV === 'development' && 
								process.env.REACT_APP_BUILD_TARGET === 'widget' &&
								!window.parent || window.parent === window;
	const [isOpened, setIsOpened] = useState<boolean>(shouldOpenByDefault);
	const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
	const [isVisible, setIsVisible] = useState<boolean>(true);
	const [currentView, setCurrentView] = useState<string>(View.Register);
	// Initialize loading state based on currentView - only Register view should start loading
	const [isFlowLoading, setIsFlowLoading] = useState<boolean>(true); // Start with true to prevent initial flash

	const [bestSize, setBestSize] = useState<{ label: string; size: string }>();
	const [bestSizeLoading, setBestSizeLoading] = useState<boolean>(false);

	const [typeLabelLong, setTypeLabelLong] = useState<string>('');
	const [typeLabelDisplay, setTypeLabelDisplay] = useState<string>('');
	const [initPageDataLoading, setInitPageDataLoading] = useState<boolean>(false);
	const [initPageDataError, setInitPageDataError] = useState<string>('');
	const [step, setStep] = useState<number>(0);
	const [visitImage, setVisitImage] = useState<string>('');
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	const [genderLabel, setGenderLabel] = useState<'male' | 'female' | 'unknown'>('unknown');
	const [showLoginModal, setShowLoginModal] = useState(false);

	const [forceShowAnalysis, setForceShowAnalysis] = useState(false);

	const modalBoxRef = useRef<HTMLDivElement | null>(null);

	// Track widget opened
	useEffect(() => {
		if (isOpened) {
			const eventName = isAuthenticated ? 'widget_auth_opened' : 'widget_unauth_opened';
			trackEvent(eventName);
		}
	}, [isOpened, isAuthenticated]);

	useEffect(() => {
		if (shouldOpenByDefault) {
			setIsOpened(true);
			(document.getElementById('widget-modal') as HTMLDialogElement)?.showModal();
		}

		const btn = buttonRef.current;
		if (!btn) return;

		const blocker = (ev: Event) => {
		ev.preventDefault();
		ev.stopPropagation();
		// @ts-ignore
		if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
		// @ts-ignore
		ev.cancelBubble = true;
		};

		const types = ['click','pointerdown','mousedown','touchstart'];
		types.forEach(t => btn.addEventListener(t, blocker, { capture: true }));

		return () => types.forEach(t => btn.removeEventListener(t, blocker, { capture: true } as any));
	}, []);

	const handleModalClose = () => {
		(document.getElementById('widget-modal') as HTMLDialogElement).close();
		setIsOpened(false);
		const getCurrentScreenName = () => {
			if (currentView === View.Register) {
				// FIXME: D.R.Y. => this should be declared just once
				switch (step) {
					case 0: return 'additemview';
					case 4: return 'brandselectionview';
					case 5: return 'productselectionview';
					default: return currentView;
				}
			}
			return currentView;
		};
		trackEvent('widget_closed_click', { screenName: getCurrentScreenName() });
		// Notify parent that modal is closed
		window.parent.postMessage({ type: 'MODAL_CLOSED' }, '*');
	};

	// Send messages back to parent
	const sendMessageToParent = useCallback((type: string, payload?: any) => {
		window.parent.postMessage({ type, payload }, '*');
	}, []);

	// Notify parent if the widget should be hidden
	useEffect(() => {
		if (!isVisible) {
			sendMessageToParent('SHOULD_HIDE');
		}
	}, [isVisible, sendMessageToParent]);

	// Send widget data updates to parent button
	useEffect(() => {
		sendMessageToParent('WIDGET_DATA_UPDATE', {
			initPageDataLoading,
			bestSizeLoading,
			isAuthenticated,
			isOnboarded,
			bestSize
		});
	}, [initPageDataLoading, bestSizeLoading, isAuthenticated, isOnboarded, bestSize, sendMessageToParent]);

	const toggleView = (view: string) => {
		setCurrentView(view);
	};

	const visit = async (token: string, partnerId: string) => {
		const currentUrl = props.currentUrl;
		const result = await visitUrl(token, partnerId, currentUrl);
		if (result?.image) setVisitImage(result.image);
		if (result?.should_hide) {
			setIsVisible(false);
		}
		if (result?.gender_label === 'male' || result?.gender_label === 'female') {
			setGenderLabel(result.gender_label);
		} else {
			setGenderLabel('unknown');
		}
		return result;
	};

	const refreshBestSize = async () => {
		console.debug('🔄 [WIDGET] Refreshing bestSize after ADD_ITEM completion');
		setBestSizeLoading(true);
		try {
			const visitResult = await visit(token, props.partnerId);
			if (visitResult && visitResult.bestSizes && visitResult.bestSizes.length > 0) {
				setBestSize(visitResult.bestSizes[0]);
				console.debug('🔄 [WIDGET] BestSize refreshed:', visitResult.bestSizes[0]);
			}
		} catch (error) {
			console.error('🔄 [WIDGET] Failed to refresh bestSize:', error);
		} finally {
			setBestSizeLoading(false);
		}
	};

	const unAuthVisit = async (partnerId: string) => {
		const currentUrl = props.currentUrl;
		const result = await unAuthVisitUrl(partnerId, currentUrl);
		if (result?.image) setVisitImage(result.image);
		if (result?.should_hide) {
			setIsVisible(false);
		}
		if (result?.gender_label === 'male' || result?.gender_label === 'female') {
			setGenderLabel(result.gender_label);
		} else {
			setGenderLabel('unknown');
		}
		return result;
	};

	const checkVisibility = useCallback(async (partnerId: string) => {
		await checkRequest(
			token,
			partnerId,
			props.currentUrl
		).then((response) => {
			if (response) {
				setIsAuthorized(true);
				setIsVisible(true);
			} else {
				setIsAuthorized(false);
				setIsVisible(false);
			}
		});
	}, [token]);

	const handleLogout = () => {
		logout();
		setBestSize(undefined);
		setCurrentView(View.Register);
	};

	const handleSaveClick = () => {
		trackEvent('save_profile_clicked');
		setShowLoginModal(true);
	};

	useEffect(() => {
		checkVisibility(props.partnerId);
	}, [checkVisibility, props.partnerId]);

	useEffect(() => {
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		const handleRouteChange = () => {
			console.debug('Route changed. Checking visibility.');
			checkVisibility(props.partnerId);
		};

		const wrapHistoryMethod = (method: 'pushState' | 'replaceState') => {
			const original = window.history[method];
			return function (...args: any) {
				const result = original.apply(window.history, args);
				window.dispatchEvent(new Event(method));
				return result;
			};
		};

		window.history.pushState = wrapHistoryMethod('pushState');
		window.history.replaceState = wrapHistoryMethod('replaceState');

		window.addEventListener('pushState', handleRouteChange);
		window.addEventListener('replaceState', handleRouteChange);
		window.addEventListener('popstate', handleRouteChange);

		return () => {
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
			window.removeEventListener('pushState', handleRouteChange);
			window.removeEventListener('replaceState', handleRouteChange);
			window.removeEventListener('popstate', handleRouteChange);
		};
	}, [checkVisibility, props.partnerId]);

	useEffect(() => {
		if (!isAuthorized) {
			return;
		}
		if (isAuthenticated && isOnboarded) {
			setBestSizeLoading(true);

			const initPageData = async (partnerId: string) => {
				setInitPageDataLoading(true);
				setInitPageDataError('');
				try {
					const visitResult = await visit(token, partnerId);
					if (!visitResult) {
						console.error('visitResult is undefined');
						return;
					}
					setTypeLabelDisplay(visitResult.type_label_display);
					const bestSizes = visitResult.bestSizes;
					
					if (bestSizes.length > 0) {
						setBestSize(bestSizes[0]);
						setBestSizeLoading(false);
						
						// Check if this is an Add Item flow (authenticated user with error bestSize)
						const bestSize = bestSizes[0];
						if (isAuthenticated && isOnboarded && bestSize?.label === 'error' && bestSize?.size === 'missing_measurements') {
							console.debug('🔧 [WIDGET] Detected Add Item flow - setting view to Register and resetting step');
							setStep(0); // Reset to step 0 for ADD_ITEM flow (AddItemView)
							setCurrentView(View.Register);
						} else {
							console.debug('🔧 [WIDGET] Valid bestSize found - setting view to SizeRecommendation');
							setCurrentView(View.SizeRecommendation);
						}
					} else {
						// In any case
						// If we are in Register → show Analysis first
							if (currentView === View.Register) {
								setForceShowAnalysis(true);
								setCurrentView(View.SizeRecommendation); // still go, but AnalysisView will render first
							} else {
								setCurrentView(View.SizeRecommendation);
							}

					setCurrentView(View.SizeRecommendation);
					}
				} catch (error: any) {
					console.error('Page data init failed:', error.message);
					setInitPageDataError(error.message);
				} finally {
					setInitPageDataLoading(false);
					setBestSizeLoading(false);
				}
			};

			initPageData(props.partnerId);
		} else {
			// TODO: dry
			const initPageDataUnAuth = async (partnerId: string) => {
				const visitResult = await unAuthVisit(partnerId);
				if (!visitResult) {
					console.error('visitResult is undefined');
					return;
				}
				let tll = visitResult.type_label_long;
				setTypeLabelDisplay(visitResult.type_label_display);
				setTypeLabelLong(tll);
			};
			initPageDataUnAuth(props.partnerId);
		}

		if (isAuthenticated && !isOnboarded) {
			setCurrentView(View.Register);
		}
	}, [isAuthenticated, isOnboarded, isAuthorized, props.partnerId, token]);

	// Add iframe message listener for communication with button
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			console.debug('Received message in widget modal:', event.data);
			// Validate origin in production
			// if (event.origin !== 'https://trusted-domain.com') return;
			
			switch (event.data.type) {
				case 'WIDGET_INIT':
					// Widget initialization data received from button
					console.debug('Widget initialized with data:', event.data.payload);
					break;
				case 'OPEN_MODAL':
					setIsOpened(true);
					(document.getElementById('widget-modal') as HTMLDialogElement)?.showModal();
					break;
				case 'CLOSE_MODAL':
					handleModalClose();
					break;
				case 'TOGGLE_MODAL':
					if (isOpened) {
						handleModalClose();
					} else {
						setIsOpened(true);
						(document.getElementById('widget-modal') as HTMLDialogElement)?.showModal();
					}
					break;
				default:
					break;
			}
		};

		window.addEventListener('message', handleMessage);
		
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [isOpened, handleModalClose]);

	// Send size recommendation back to parent
	const handleSizeRecommendation = useCallback((size: string) => {
		sendMessageToParent('SIZE_RECOMMENDATION', { size });
	}, [sendMessageToParent]);

	// Handle gender toggle from WidgetHeader
	const handleGenderToggle = useCallback(() => {
		const currentUserGender = getGenderFromShoppingPreference(shoppingPreference || '');
		const newGender = currentUserGender === 'male' ? 'female' : 'male';
		
		console.debug('🔧 [WIDGET_MODAL] Gender toggled from', currentUserGender, 'to', newGender);
		const newShoppingPreference = getShoppingPreferenceFromGender(newGender);
		setShoppingPreference(newShoppingPreference);
		console.debug('🔧 [WIDGET_MODAL] Shopping preference updated to:', newShoppingPreference);
	}, [shoppingPreference, setShoppingPreference]);

	// Handle flow loading state changes from RegisterView
	const handleFlowLoadingChange = useCallback((isLoading: boolean) => {
		console.debug('🔧 [WIDGET_MODAL] Flow loading state changed:', isLoading);
		setIsFlowLoading(isLoading);
	}, []);

	// Track isFlowLoading state changes
	useEffect(() => {
		console.debug('🔧 [WIDGET_MODAL] isFlowLoading state updated to:', isFlowLoading);
	}, [isFlowLoading]);

	// Reset flow loading when view changes away from Register
	useEffect(() => {
		if (currentView !== View.Register) {
			console.debug('🔧 [WIDGET_MODAL] View changed to non-Register view, resetting flow loading to false');
			setIsFlowLoading(false);
		}
	}, [currentView]);

	return (
		<>
			{!initPageDataError && isVisible && (
				<>
					<dialog id="widget-modal" className="pop-modal">
					<div
						ref={modalBoxRef}
						className={`pop-hide-scrollbar pop-modal-box pop-flex pop-flex-col pop-bg-popsizeSecondary10 pop-relative ${
							(currentView === View.Login || currentView === View.SizeRecommendation)
								? 'pop-w-[33%] pop-h-[75%] pop-sm:pop-w-[80%]'
								: 'pop-w-[50%] pop-h-[80%] pop-sm:pop-w-[80%]'
						}`}
					>
						{/* Close Button - positioned at top-right of entire modal */}
						<button 
							onClick={handleModalClose} 
							className="pop-absolute pop-top-1 pop-right-1 pop-z-10"
						>
							<IoClose size={24} fill="rgb(var(--pop-primary-90))" />
						</button>
						{currentView === View.Register && (
							<>
								{/* 15% Header - only show when not loading */}
								{!isFlowLoading ? (() => {
									console.debug('🎨 [WIDGET_MODAL] WidgetHeader is being rendered (isFlowLoading:', isFlowLoading, ')');
									return (
										<div className="pop-h-[15%] pop-w-full">
											<WidgetHeader
												brandLogoUrl={props.brandLogoUrl}
												shoppingPreferenceLabel={props.shoppingPreferenceLabel}
												centerImageUrl={visitImage}
												productGenderLabel={genderLabel}
												userGenderLabel={getGenderFromShoppingPreference(shoppingPreference || '')}
												genderSpecific={props.genderSpecific}
												currentItem={bestSize}
												isAuthenticated={isAuthenticated}
												isOnboarded={isOnboarded}
												onGenderToggle={handleGenderToggle}
											/>
										</div>
									);
								})() : (() => {
									console.debug('🎨 [WIDGET_MODAL] WidgetHeader is HIDDEN (isFlowLoading:', isFlowLoading, ')');
									return null;
								})()}

								{/* 5% ProgressBar - only show when not loading */}
								{!isFlowLoading ? (() => {
									console.debug('🎨 [WIDGET_MODAL] ProgressBar is being rendered (isFlowLoading:', isFlowLoading, ')');
									return (
										<div className="pop-h-[5%] pop-w-full pop-flex pop-items-center pop-justify-center pop-my-2">
											{isOpened && step >= 4 && step <= 6 ? (
												<ProgressBar
													currentStep={
														step === 4 ? 0 :
														step === 5 ? 1 :
														-1
													}
													stepLabels={[
														t('brands_step'),
														t('products_step'),
													]}
												/>
											) : (
												<div /> // Preserve layout
											)}
										</div>
									);
								})() : (() => {
									console.debug('🎨 [WIDGET_MODAL] ProgressBar is HIDDEN (isFlowLoading:', isFlowLoading, ')');
									return null;
								})()}

								{/* 80% RegisterView - adjust height when loading */}
								<div className={`pop-w-full pop-overflow-y-auto pop-hide-scrollbar ${isFlowLoading ? 'pop-h-full' : 'pop-h-full'}`}>
									{(() => {
										const isAddItemFlowCondition = isAuthenticated && isOnboarded && bestSize?.label === 'error' && bestSize?.size === 'missing_measurements';
										const isNewUserAddItemFlowCondition = isAuthenticated && !isOnboarded;
										console.debug('🔍 [WIDGET] Flow condition check:', {
											isAuthenticated,
											isOnboarded,
											bestSizeLabel: bestSize?.label,
											bestSizeSize: bestSize?.size,
											isAddItemFlowCondition,
											isNewUserAddItemFlowCondition
										});
										return (
											<RegisterView
												modalIsOpen={isOpened}
												toggleView={toggleView}
												typeLabelLong={typeLabelLong}
												typeLabelDisplay={typeLabelDisplay}
												genderSpecific={props.genderSpecific}
												partnerId={props.partnerId}
												step={step}
												setStep={setStep}
												centerImageUrl={visitImage}
												genderLabel={genderLabel}
												currentView={currentView}
												isAddItemFlow={isAddItemFlowCondition}
												isNewUserAddItemFlow={isNewUserAddItemFlowCondition}
												refreshBestSize={refreshBestSize}
												onFlowLoadingChange={handleFlowLoadingChange}
											/>
										);
									})()}
								</div>
							</>
						)}

						{/* LoginView */}
						{currentView === View.Login && (
							<LoginView modalIsOpen={isOpened} toggleView={toggleView} />
						)}

						{/* ResetPasswordView */}
						{currentView === View.RestPassword && (
							<ResetPasswordView modalIsOpen={isOpened} toggleView={toggleView} />
						)}

						{/* AnalysisView */}
						{currentView === View.SizeRecommendation && !bestSize && (
							<AnalysisView
								handleNextStep={() => {}}
								modalIsOpen={isOpened}
								toggleView={toggleView}
								centerImageUrl={visitImage}
								bestSizeReady={!!bestSize}
								refreshBestSize={refreshBestSize}
							/>
						)}

						{/* SizeRecommandationView */}
						{currentView === View.SizeRecommendation && bestSize && forceShowAnalysis && (
						<AnalysisView
							modalIsOpen={isOpened}
							toggleView={toggleView}
							centerImageUrl={visitImage}
							bestSizeReady={!!bestSize}
							refreshBestSize={refreshBestSize}
						/>
						)}

						{currentView === View.SizeRecommendation && bestSize && !forceShowAnalysis && (
						<SizeRecommandationView
							modalIsOpen={isOpened}
							toggleView={toggleView}
							bestSize={bestSize}
							handleLogout={handleLogout}
							handleSaveClick={handleSaveClick}
						/>
						)}

						{/* Login Modal (non-blocking) */}
						{showLoginModal && (
							<div
								className="pop-fixed pop-inset-0 pop-bg-black/40 pop-z-[9999] pop-flex pop-justify-center pop-items-center"
								onClick={() => setShowLoginModal(false)}
							>
								<div
									className="pop-bg-white pop-rounded-xl pop-p-6 pop-shadow-lg pop-max-w-md pop-w-[90%]"
									onClick={(e) => e.stopPropagation()}
								>
									<LoginView modalIsOpen={true} toggleView={toggleView} />
								</div>
							</div>
						)}


					</div>

					{/* Backdrop */}
					<form method="dialog" className="pop-modal-backdrop" onClick={handleModalClose}>
						<button>{t(`close`)}</button>
					</form>
				</dialog>

				</>
			)}
		</>
	);
};

export default WidgetModal;
